import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Clock, ExternalLink } from 'lucide-react';
import api from '../../services/api';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const redirectUrl = searchParams.get('redirect');

  const [status, setStatus] = useState(sessionId ? 'polling' : 'init');
  const [attempts, setAttempts] = useState(0);
  const [countdown, setCountdown] = useState(5);
  const [showConfetti, setShowConfetti] = useState(false);
  const polling = useRef(false);
  const maxAttempts = 12;

  // Polling du statut de paiement
  useEffect(() => {
    if (!sessionId || polling.current) return;
    polling.current = true;

    const pollStatus = async (attempt) => {
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }
      try {
        const res = await api.get(`/payment/checkout/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          setShowConfetti(true);
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        setAttempts(attempt + 1);
        setTimeout(() => pollStatus(attempt + 1), 2000);
      } catch {
        if (attempt < maxAttempts - 1) {
          setTimeout(() => pollStatus(attempt + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };
    pollStatus(0);
  }, [sessionId]);

  // Countdown + redirection vers Site A
  useEffect(() => {
    if (status !== 'success' || !redirectUrl) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = redirectUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [status, redirectUrl]);

  if (!sessionId) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="bg-white rounded-xl p-8 border border-gray-200 max-w-md w-full text-center shadow-sm">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-sm text-gray-500 mb-6">Session de paiement introuvable.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 relative overflow-hidden">

      {/* Confetti animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                width: `${6 + Math.random() * 8}px`,
                height: `${6 + Math.random() * 8}px`,
                backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'][i % 6],
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl p-8 border border-gray-200 max-w-md w-full text-center shadow-lg relative z-10">

        {/* POLLING */}
        {(status === 'init' || status === 'polling') && (
          <>
            <div className="relative mx-auto mb-6 w-20 h-20">
              <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
              <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
              <Loader2 className="absolute inset-0 m-auto h-8 w-8 text-blue-600 animate-pulse" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Vérification du paiement...</h2>
            <p className="text-sm text-gray-500">Veuillez patienter pendant que nous confirmons votre paiement.</p>
            <div className="mt-4 bg-gray-50 rounded-lg p-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${((attempts + 1) / maxAttempts) * 100}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">Tentative {attempts + 1}/{maxAttempts}</p>
            </div>
          </>
        )}

        {/* SUCCESS */}
        {status === 'success' && (
          <div className="animate-fadeInUp">
            <div className="mx-auto mb-6 w-20 h-20 relative">
              <div className="absolute inset-0 rounded-full bg-green-100 animate-ping-slow" />
              <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-green-100">
                <CheckCircle className="h-12 w-12 text-green-500 animate-scaleIn" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Paiement confirmé !</h2>
            <p className="text-sm text-gray-500 mb-6">
              Merci pour votre achat. Votre abonnement est en cours d'activation.
            </p>

            {redirectUrl && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <p className="text-sm text-blue-700 font-medium mb-2">
                    Redirection automatique dans
                  </p>
                  <div className="relative mx-auto w-14 h-14">
                    <svg className="w-14 h-14 transform -rotate-90" viewBox="0 0 56 56">
                      <circle cx="28" cy="28" r="24" fill="none" stroke="#DBEAFE" strokeWidth="4" />
                      <circle
                        cx="28" cy="28" r="24" fill="none" stroke="#3B82F6" strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 24}
                        strokeDashoffset={2 * Math.PI * 24 * (1 - countdown / 5)}
                        className="transition-all duration-1000 ease-linear"
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-blue-600">
                      {countdown}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => { window.location.href = redirectUrl; }}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  Se connecter maintenant
                </button>
              </div>
            )}
          </div>
        )}

        {/* EXPIRED */}
        {status === 'expired' && (
          <>
            <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Session expirée</h2>
            <p className="text-sm text-gray-500 mb-6">La session de paiement a expiré. Veuillez réessayer.</p>
          </>
        )}

        {/* ERROR / TIMEOUT */}
        {(status === 'error' || status === 'timeout') && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Erreur de vérification</h2>
            <p className="text-sm text-gray-500 mb-6">
              {status === 'timeout'
                ? "La vérification a pris trop de temps. Si vous avez été débité, votre accès sera activé automatiquement."
                : "Une erreur est survenue lors de la vérification du paiement."}
            </p>
            {redirectUrl && (
              <button
                onClick={() => { window.location.href = redirectUrl; }}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Retour à la connexion
              </button>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes confetti {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        .animate-confetti {
          animation: confetti 3s ease-in-out forwards;
        }
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        @keyframes scaleIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        .animate-scaleIn {
          animation: scaleIn 0.5s ease-out 0.2s both;
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.5; }
          75%, 100% { transform: scale(1.5); opacity: 0; }
        }
        .animate-ping-slow {
          animation: ping-slow 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccessPage;
